/**
 * SysAdmin Enhetsköer — frontend API-klient
 */

import api from '../api/client';

export type DeviceQueueType = 'session' | 'command' | 'pnp' | 'force_non_obd';
export type DeviceQueueView = 'queue' | 'done';

export interface DeviceQueueCounts {
  queue: number;
  done: number;
}

export interface DeviceQueueItem {
  id: string;
  queueType: DeviceQueueType;
  imei: string;
  registrationNumber: string | null;
  makeModel: string | null;
  groupName: string | null;
  currentFirmware: string | null;
  lastSeen: string | null;
  addedAt: string;
  purposeOrStatus: string;
  detail: string | null;
  commandCount: number | null;
  vehicleId: number | null;
  organizationId: number | null;
}

export interface ForceQueueResult {
  success: boolean;
  message: string;
  data?: {
    offline?: boolean;
    queued?: boolean;
    deliveredLive?: boolean;
    imei?: string;
    [key: string]: unknown;
  };
}

export interface GetverResult {
  imei: string;
  delivered: boolean;
  queued: boolean;
  response: string | null;
  firmware: string | null;
  message: string;
}

const BASE = '/api/sysadmin/device-queues';

export const deviceQueueService = {
  async list(params?: {
    types?: DeviceQueueType[];
    search?: string;
    organizationId?: string;
    view?: DeviceQueueView;
  }): Promise<{ items: DeviceQueueItem[]; counts: DeviceQueueCounts }> {
    const query: Record<string, string> = {};
    if (params?.types?.length) query.types = params.types.join(',');
    if (params?.search) query.search = params.search;
    if (params?.organizationId) query.organizationId = params.organizationId;
    if (params?.view) query.view = params.view;
    const res = await api.get(BASE, { params: query });
    const countsRaw = res.data?.counts ?? {};
    return {
      items: (res.data?.data ?? []) as DeviceQueueItem[],
      counts: {
        queue: Number(countsRaw.queue ?? 0),
        done: Number(countsRaw.done ?? 0),
      },
    };
  },

  async force(queueType: DeviceQueueType, id: string): Promise<ForceQueueResult> {
    const res = await api.post(`${BASE}/${encodeURIComponent(queueType)}/${encodeURIComponent(id)}/force`);
    return {
      success: Boolean(res.data?.success),
      message: res.data?.message || res.data?.data?.message || '',
      data: res.data?.data,
    };
  },

  async remove(queueType: DeviceQueueType, id: string): Promise<{ message: string }> {
    const res = await api.delete(`${BASE}/${encodeURIComponent(queueType)}/${encodeURIComponent(id)}`);
    return { message: res.data?.message || 'OK' };
  },

  async getver(imei: string): Promise<GetverResult> {
    const res = await api.post(`${BASE}/imei/${encodeURIComponent(imei)}/getver`);
    return res.data?.data as GetverResult;
  },

  async markInteresting(imei: string): Promise<{ message: string }> {
    const res = await api.post(`${BASE}/mark`, { imei });
    return { message: res.data?.message || 'OK' };
  },
};

export default deviceQueueService;
